import { Row, Col } from 'antd';
import { useFormikContext } from 'formik';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '..';
import { type ListFinderVehiclesQueryVariables } from '../../../../../api/queries/listFinderVehicles';
import InputField from '../../../../../components/fields/InputField';
import InputRangeNumberField from '../../../../../components/fields/InputRangeNumberField';
import SelectField from '../../../../../components/fields/SelectField';
import { type TransferFieldProps } from '../../../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../../../components/wrappers/CollapsibleWrapper';
import { withCommasForNumberString } from '../../../../../utilities/useFormats';
import useSystemOptions from '../../../../../utilities/useSystemOptions';
import { mileageFormatAmount } from '../../../AddVehiclePage/AddVariantPage/helper';
import SyncFinderVehicleButton from '../../../FinderVehicleListPage/SyncFinderVehicleButton';
import YesNoField from '../ParametersForm/YesNoField';
import StyledTransferField from './StyledTransferField';
import useFinanceProductEngineTypeOptions from './useFinanceProductEngineTypeOptions';
import { hasVehicle, useFinderVehicles } from './useVehicles';

const colSpan = { lg: 8, xs: 24 };

type RecordType = {
    key: string;
    title: string;
};

type FinderVehicleAssignmentsSectionProps = {
    header: ReactNode;
    name: string;
    filter?: ListFinderVehiclesQueryVariables['filter'];
    transferFieldProps?: Omit<TransferFieldProps<RecordType>, 'name' | 'dataSource'>;
    allowSync: boolean;
    disabled?: boolean;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const FinderVehicleAssignmentsSection = ({
    header,
    name,
    filter,
    transferFieldProps,
    allowSync,
    disabled = false,
}: FinderVehicleAssignmentsSectionProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const vehicleDataSource = useFinderVehicles(filter);

    const engineTypeOptions = useFinanceProductEngineTypeOptions();
    const { finderVehicelConditionOptions } = useSystemOptions();

    const mileageFormatter = useCallback((value, { userTyping }) => {
        if (!value) {
            return null;
        }

        return userTyping ? withCommasForNumberString(value) : mileageFormatAmount(value);
    }, []);

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();
    const { isAddOnSpecificVehicles } = values?.vehicleReferenceParameters?.finderVehicle || {};
    useEffect(() => {
        if (!isAddOnSpecificVehicles) {
            const { variantSuiteIds } = values;
            setFieldValue(
                'variantSuiteIds',
                variantSuiteIds.filter(id => !hasVehicle(vehicleDataSource, id))
            );
        }
    }, [isAddOnSpecificVehicles]);

    return (
        <CollapsibleWrapper defaultActiveKey={name}>
            <Panel key={name} header={header}>
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <SelectField
                            {...t('financeProductDetails:fields.matchEngineType', { returnObjects: true })}
                            disabled={disabled}
                            mode="multiple"
                            name="vehicleReferenceParameters.finderVehicle.engineType"
                            options={engineTypeOptions}
                            showSearch
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputField
                            {...t('financeProductDetails:fields.matchVariant', { returnObjects: true })}
                            disabled={disabled}
                            name="vehicleReferenceParameters.finderVehicle.variants"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputRangeNumberField
                            {...t('financeProductDetails:fields.matchMileage', { returnObjects: true })}
                            addonAfter={t('financeProductDetails:suffix.mileage')}
                            disabled={disabled}
                            formatter={mileageFormatter}
                            name="vehicleReferenceParameters.finderVehicle.mileage"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputRangeNumberField
                            {...t('financeProductDetails:fields.matchModelYear', { returnObjects: true })}
                            disabled={disabled}
                            maxLength={4}
                            name="vehicleReferenceParameters.finderVehicle.modelYear"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SelectField
                            {...t('financeProductDetails:fields.matchCondition', { returnObjects: true })}
                            disabled={disabled}
                            maxLength={4}
                            mode="multiple"
                            name="vehicleReferenceParameters.finderVehicle.conditions"
                            options={finderVehicelConditionOptions}
                            showSearch
                        />
                    </Col>
                    <Col {...colSpan}>
                        <YesNoField
                            {...t('financeProductDetails:fields.isAddOnSpecificVehicles', { returnObjects: true })}
                            disabled={disabled}
                            name="vehicleReferenceParameters.finderVehicle.isAddOnSpecificVehicles"
                        />
                    </Col>
                </Row>
                {isAddOnSpecificVehicles && (
                    <Row gutter={14}>
                        <Col lg={16} xs={24}>
                            <StyledTransferField
                                dataSource={vehicleDataSource}
                                disabled={disabled}
                                filterOption={filterOption}
                                name={name}
                                render={(item: RecordType) => item.title}
                                {...transferFieldProps}
                            />
                        </Col>
                    </Row>
                )}

                {filter?.companyId && allowSync && !disabled && (
                    <Row gutter={14}>
                        <Col>
                            <SyncFinderVehicleButton companyId={filter?.companyId} />
                        </Col>
                    </Row>
                )}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default FinderVehicleAssignmentsSection;
